import axios from "axios";
import ReactNativeMessageHandler from "../../ReactNativeMessageHandler";
import { THREE_INST } from "../Three/threeSingleton";
import LogRocket from "logrocket";

const three = THREE_INST.getInst();

class MovieMaker {
  mediaSource?: MediaSource;
  canvasTarget?: HTMLCanvasElement;
  clonedCanvas?: HTMLCanvasElement;
  backgroundImage?: HTMLImageElement;
  auth?: string;
  previewCanvasTarget?: HTMLCanvasElement;
  sourceBuffer?: SourceBuffer;
  recordedBlobs: any = [];
  renderBlobs: any = [];
  mediaRecorder?: MediaRecorder;
  currentState: string = "";

  isRendering = false;
  requestId?: number = -1;

  recordingEncoding = "";
  title: string = "";
  length: number = 0;

  eventHandler?: (e: string, warg?: any) => void;
  setProgress?: (progress: number) => void;

  setRecordingState = (state: string) => {
    this.currentState = state;
    this.eventHandler!(state);
  };

  init = (
    canvasTarget: HTMLCanvasElement,
    length: number = 0,
    title: string = "",
    backgroundImage?: HTMLImageElement,
    forcePlay: boolean = false,
    auth: string = ""
  ) => {
    if (forcePlay) {
      this.requestId = -1;
      this.backgroundImage = backgroundImage;
      this.title = title;
      this.length = length;
    }
    this.mediaSource = new MediaSource();

    this.auth = auth;

    this.canvasTarget = canvasTarget;
    this.clonedCanvas = this.canvasTarget.cloneNode() as HTMLCanvasElement;

    three.obj.handleStreaming(true);

    if (!this.canvasTarget || !this.canvasTarget.captureStream) return;
    const stream = this.canvasTarget.captureStream(20);

    this.initRecording(stream, forcePlay);
  };

  handleDataAvailable = (event: any) => {
    if (event.data && event.data.size > 0) {
      if (this.isRendering) {
        this.renderBlobs.push(event.data);
        this.downloadRender();
      } else {
        this.recordedBlobs.push(event.data);
      }
    }
  };
  getSupportedMineType =  (): string =>  {
    const test = [
      'video/webm; codecs=h264',
      'video/mp4; codecs="avc1.4d002a',
      'video/mp4; codecs="avc1.424028, mp4a.40.2"',
      'video/mp4',
      'video/webm;codecs=vp8',
      'video/webm,codecs=vp9',
      'codecs=vp9',
      'video/vp8'
    ]
    for ( let i in test ) {
      if ( MediaRecorder.isTypeSupported( test[i] ) ){
        return test[i];
      }
    }
    return 'video/webm;codecs=vp8';
  }

  initRecording = (stream: MediaStream, forcePlay = false) => {
    this.recordingEncoding = this.getSupportedMineType();
    console.info("Supported Encoding: " + this.recordingEncoding )
    let options: object | string = {
      mimeType: this.recordingEncoding,
      videoBitsPerSecond: 2500000,
    };
    if (stream === undefined) return;

    try {
      this.mediaRecorder = new MediaRecorder(stream, options);
    } catch (e0) {
      options = "video/vp8"; // Chrome 47
      this.mediaRecorder = new MediaRecorder(
        stream,
        options as MediaRecorderOptions
      );
    }

    this.mediaRecorder.ondataavailable = this.handleDataAvailable;
    if (forcePlay) this.mediaRecorder.start();
  };

  stopRecording = () => {
    if (this.mediaRecorder === undefined) return;
    this.mediaRecorder.stop();
  };

  displayPreview = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
    const blob = new Blob(this.recordedBlobs, {
      type: this.recordingEncoding,
    });
    const url = window.URL.createObjectURL(blob);
    const video = document.createElement("video");
    video.loop = true;
    video.src = url;
    video.load();
    video.onloadedmetadata = (evt) => {
      video.currentTime = 10e6;
      video.addEventListener("seeked", () => (video.currentTime = 0), {
        once: true,
      });
    };
    const promise = video.play();

    if (promise !== undefined) {
      promise.then(() => {
        this.setRecordingState("PREVIEWING");

        const step = () => {
          ctx.clearRect(0, 0, w, h);
          if (this.backgroundImage) {
            ctx.drawImage(this.backgroundImage as any, 0, 0, w, h);
          }
          ctx.drawImage(video, 0, 0, w, h);
          if (this.requestId) {
            requestAnimationFrame(step);
          } else {
            window.URL.revokeObjectURL(url);
          }
        };
        this.requestId = requestAnimationFrame(step);
      });
    }
  };

  downloadRender = () => {
    const blob = new Blob(this.renderBlobs, {
      type: this.recordingEncoding,
    });
    const title = `ArtThread_${this.title}.webm`;

    if (window.isMobileSplash && window.ReactNativeWebView) {
      const endpoint =
        process.env.NODE_ENV === "development"
          ? "/formUpload"
          : "/formUpload";

      const data = new FormData();
      data.append("file", blob, title);
      data.append("auth", this.auth || "");

      axios
        .post(endpoint, data, {
          headers: {
            "content-type": "multipart/form-data",
          },
        })
        .then((res) => {
          ReactNativeMessageHandler.postMessage(
            {
              url: res.data,
              auth: this.auth,
              name: title,
            },
            "downloadVideo"
          );
        })
        .catch((err) => {
          LogRocket.log("Error uploading movie", err);
        });
    } else {
      console.info("rendering clickable link");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = title;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
    this.cleanup();
  };

  renderDownload = () => {
    this.setRecordingState("DOWNLOADING");
    const blob = new Blob(this.recordedBlobs, {
      type: this.recordingEncoding,
    });
    const url = window.URL.createObjectURL(blob);
    const video = document.createElement("video");

    video.onended = () => {
      this.mediaRecorder!.stop();
    };

    video.loop = false;
    video.src = url;
    video.load();

    const promise = video.play();
    video.onloadedmetadata = (evt) => {
      video.currentTime = 10e6;
      video.addEventListener("seeked", () => (video.currentTime = 0), {
        once: true,
      });
    };

    const ctx = this.clonedCanvas!.getContext("2d");
    const { width, height } = this.clonedCanvas!;

    if (promise !== undefined && ctx !== null) {
      this.mediaRecorder!.start();
      promise.then(() => {
        this.isRendering = true;

        const step = () => {
          ctx.clearRect(0, 0, width, height);
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, width, height);
          if (this.backgroundImage) {
            ctx.drawImage(this.backgroundImage as any, 0, 0, width, height);
          }
          ctx.drawImage(video, 0, 0, width, height);
          if (this.requestId) {
            this.requestId = requestAnimationFrame(step);
          }
        };
        this.requestId = requestAnimationFrame(step);
      });
    }
  };

  initDownload = () => {
    console.info("init downloading....")
    if (this.clonedCanvas && this.currentState !== "SHOW_PREVIEW") {
      this.setRecordingState("SHOW_PREVIEW");
      const stream = this.clonedCanvas!.captureStream(20);
      this.initRecording(stream);
      this.renderDownload();
    }
  };

  cleanup = () => {
    three.obj.handleStreaming(false);
    this.renderBlobs = [];
    this.recordedBlobs = [];
    this.isRendering = false;
    if (this.requestId) {
      window.cancelAnimationFrame(this.requestId);
    }
    this.requestId = undefined;
    this.setRecordingState("COMPLETE");
  };
}

export class MOVIE_MAKER {
  private static instance: MOVIE_MAKER;
  private static mmClass: MovieMaker;

  constructor() {
    MOVIE_MAKER.mmClass = new MovieMaker();
  }

  get obj() {
    return MOVIE_MAKER.mmClass;
  }

  static getInst(): MOVIE_MAKER {
    if (!this.instance) {
      this.instance = new MOVIE_MAKER();
    }
    return this.instance;
  }
}
